import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { gqlSchema } from '../../../../gql/schema';
import { useMutation, useQuery } from '@apollo/client';
import * as S from '../styles';
import {
    Avatar, Col, message, Modal, Popconfirm, Popover, Row, Spin,
    // Tooltip
} from 'antd';
import { FiDownload, FiLock } from 'react-icons/fi';
import { themeConfig } from '../../../../utils/theme';
import Button from '../../../../shared/Button';
// import { FaLock } from 'react-icons/fa';
import NewInvestigationModal from '../../../AdiTemplatesPage/NewInvestigationModal';
import { GQL_InvestigationCatalogEntry, InvestigationType, WorkShopTemplate } from '../../../../types/investigation';
import Switch from '../../../../shared/Switch';
import { BiCollapse, BiCollapseAlt } from 'react-icons/bi';

interface InvestigationTabProps {
    data: any;
    cloneText?: string;
    loading?: boolean;
    action: () => void;
    actionText: string;
    available?: boolean;
    supportedStates?: { state: string; statements: string[] }[];
    isAssessment?: boolean;
    showPreview?: boolean;
    isAdvancedSearch?: boolean;
    actionDisabled?: boolean;
    isTeacher?: boolean;
    isActive?: boolean;
    setFullScreen?: () => void;
    closeFullScreen?: () => void;
}
const downloadFile = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
const InvestigationTab: React.FC<InvestigationTabProps> = ({
    data,
    loading,
    actionText,
    cloneText,
    action,
    isTeacher = false,
    available = true,
    isAssessment = false,
    showPreview = false,
    isAdvancedSearch = false,
    actionDisabled = false,
    isActive,
    setFullScreen,
    closeFullScreen
}) => {
    const history = useHistory();
    const [openMaterials, setOpenMaterials] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // const [toggleState, setToggleState] = useState(false);
    const [, setPreviewOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const [isCloning, setIsCloning] = useState(false);
    const [isTemplate, setIsTemplate] = useState<boolean>(data.isTemplate || false);

    const [getAllMaterials] = useMutation(
        gqlSchema.InvestigationSchema.mutations.SETTINGS.getAllInvestigationMaterialsForBooks,
        {
            onCompleted: (data) => {
                setDownloading(false);
                downloadFile(data.getAllInvestigationMaterialsForBooks.fileUrl, 'Investigation-allMaterials.zip');
            },
            onError: (error) => {
                setDownloading(false);
                message.error(
                    error.message ||
                    'There was an error downloading all materials, please check your connection and try again later',
                );
            },
            fetchPolicy: 'network-only',
        },
    );

    const [cloneInvestigationFromTemplate, { loading: loadingCreation }] = useMutation<{
        cloneInvestigation: GQL_InvestigationCatalogEntry;
    }>(gqlSchema.InvestigationSchema.mutations.DRAFT.cloneInvestigation, {
        onError: (err) => {
            message.error(err.message || 'Unexpected error');
        },
        onCompleted: ({ cloneInvestigation }) => {
            isAssessment
                ? history.push(`/adi-assessments/edit/${cloneInvestigation.id}/core`)
                : history.push(`/adi-investigations/edit/${cloneInvestigation.id}/core`);
        },
        refetchQueries: ['getInvestigationDrafts', 'getInvestigationCatalog'],
    });

    const handleUseTemplate = useCallback(
        (name: string, type: InvestigationType, workShopTemplate?: WorkShopTemplate, isAssessment = false) => {
            cloneInvestigationFromTemplate({
                variables: {
                    cloneinvestigationDraft: {
                        id: data.id,
                        title: name,
                        description: data?.description || isAssessment ? '' : 'Investigation Description',
                        type,
                        workShopTemplate,
                        isAssessment,
                    },
                },
            });
        },
        [cloneInvestigationFromTemplate, data],
    );

    const toggleTemplate = useCallback(() => {
        setIsTemplate((prevState) => !prevState);
    }, []);


    const [updateInvestigationTemplate, { loading: loadingIsTemplate }] = useMutation<{ editInvestigationIsTemplate: GQL_InvestigationCatalogEntry }>(
        gqlSchema.InvestigationSchema.mutations.EDIT.editInvestigationIsTemplate,
        {
            onError: (err) => {
                message.error(err.message || 'Unexpected error');
            },
            onCompleted: (data) => {
                toggleTemplate();
            },
        }
    );

    const handleToggleTemplate = useCallback(() => {
        updateInvestigationTemplate({
            variables: {
                data: {
                    investigationId: data.id,
                    isTemplate: !isTemplate,
                },
            },
        });
    }, [updateInvestigationTemplate, data.id, isTemplate]);

    const { data: initialCacheQuery, startPolling, stopPolling } = useQuery(
        gqlSchema.InvestigationSchema.queries.DASHBOARD.getInvestigationMaterialsDownloadCacheBooks,
        {
            variables: { id: data.id },
        },
    );

    const initialCacheStatus = initialCacheQuery?.getInvestigationMaterialsDownloadCache;
    const cacheUrl = initialCacheStatus?.fileUrl;

    const downloadAllMaterials = useCallback(() => {
        setDownloading(true);

        if (!cacheUrl) {
            startPolling(5000);
            getAllMaterials({ variables: { id: data.id } });
            setIsPolling(true);
        } else {
            setTimeout(() => {
                setDownloading(false);
                window.location.href = cacheUrl;
            }, 3000);
        }
    }, [data.id, cacheUrl, startPolling, getAllMaterials]);

    useEffect(() => {
        if (isPolling && cacheUrl) {
            setIsPolling(false);
            stopPolling();
            setDownloading(false);
            window.location.href = cacheUrl;
        }
    }, [cacheUrl, isPolling, stopPolling]);

    const subscriptionPage = useCallback(() => {
        history.push('/manage-subscription');
    }, [history]);

    const cloneAction = useCallback(() => {
        setIsCloning(true);
        setModalOpen(true);
    }, [setIsCloning, setModalOpen]);

    const closeModal = useCallback(() => {
        setIsCloning(false);
        setModalOpen(false);
    }, [setIsCloning, setModalOpen]);

    const handleIconClick = () => {

        if (isActive) {
            if (closeFullScreen) {
                closeFullScreen();
            }
        } else {
            if (setFullScreen) {
                setFullScreen();
            }
        }
    };

    return (
        <>
            <S.GradeText>{data.grade}</S.GradeText>
            <Row align="middle" style={{ marginTop: 20 }}>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start', marginLeft: 10, marginTop: -120 }}>
                    {!loadingIsTemplate && !isTeacher && actionText === 'Edit Investigation' && (
                        <Popconfirm
                            placement="topLeft"
                            title={`Are you sure you want to ${isTemplate ? 'disable' : 'enable'} template?`}
                            onConfirm={handleToggleTemplate}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Switch
                                type="text"
                                title={<S.TitleInput>Enable as Template</S.TitleInput>}
                                checked={isTemplate}
                                value={isTemplate}
                                titleAlign="left"
                            />
                        </Popconfirm>
                    )}
                </div>
                <div style={{ flex: 'none', marginLeft: 10 }}>
                    <Avatar
                        src={
                            data.imageUrl ||
                            'https://cdn.shopify.com/s/files/1/0598/7762/1933/files/7th_grade_texas-1.png?v=1731777175'
                        }
                        size={105}
                    />
                </div>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', marginRight: 30, marginTop: -144 }}>
                    <S.Icon isTeacher={isTeacher} onClick={handleIconClick}>
                        {isActive ? <BiCollapseAlt /> : <BiCollapse />}
                    </S.Icon>
                </div>
            </Row>
            <Row style={{ height: 70, marginTop: 20, justifyContent: 'center' }}>
                <Col>
                    <S.Title>{data.title}</S.Title>
                </Col>
            </Row>
            <Row style={{ height: 100, overflow: 'auto', marginTop: 10, paddingTop: 20 }}>
                <Col span={24}>
                    <S.Description dangerouslySetInnerHTML={{ __html: data.description || '' }} />
                </Col>
            </Row>
            {isTeacher && <Row justify="center" align="middle" gutter={[6, 6]} style={{ marginTop: 5,
                padding:10, width: showPreview ? '80%' : '' }}>
                <Col xxl={12} xl={12} sm={24} xs={24}>
                <S.ActionButtonContainer>
                    <Button
                        minHeight={50}
                        block
                        text="View Investigation"
                        onClick={() =>{
                            console.log("hiss",history.location,"idd",data.id);
                            console.log("ggghh",`${history.location.pathname}/${data.id}`);
                            const nextPath = `${history.location.pathname.replace(/^\/test/, '')}/${data.id}`;
                            history.push(nextPath)

                        } }
                    // customTheme={themeConfig.primaryOutlined}
                    />
                    </S.ActionButtonContainer>
                </Col>
                <Col xxl={12} xl={12} sm={24} xs={24}>
                        <div style={{ width: '100%' }}>
                            <S.ActionButtonContainer>
                            <Button
                        minHeight={50}
                        block
                        text="Assign Investigation"
                        onClick={() =>{
                            const nextPath = `${history.location.pathname.replace(/^\/test/, '')}/${data.id}/assign`;
                            history.push(nextPath)

                        } }
                    // customTheme={themeConfig.primaryOutlined}
                    />
                    </S.ActionButtonContainer>
                        </div>
                </Col>

            </Row>}
            {isTeacher && (

            <Row justify="center" gutter={[6, 6]} style={{ marginTop: 5,padding:10, width: showPreview ? '80%' : '' }}>
                <Col xxl={12} xl={12} sm={24} xs={24}>
                <S.ActionButtonContainer>
                    <Button
                        minHeight={50}
                        block
                        text="Handouts"
                        onClick={() => setOpenMaterials(true)}
                    // customTheme={themeConfig.primaryOutlined}
                    />
                    </S.ActionButtonContainer>
                </Col>
                <Col xxl={12} xl={12} sm={24} xs={24}>
                <S.ActionButtonContainer>
                    <Button
                        minHeight={50}
                        block
                        text="Presentation Mode"
                        onClick={() => {
                            if (data?.id) {
                                history.push(`/investigation-presentation/${data.id}`, {
                                    state: { backUrl: window.location.pathname },
                                });
                            }
                        }}
                    // customTheme={themeConfig.primaryOutlined}
                    />
                    </S.ActionButtonContainer>
                </Col>
                {/* <Col span={8}>
                    <Tooltip title="This feature is only available for organization accounts" placement="bottom">
                        <div style={{ width: '100%' }}>
                            <Button
                                minHeight={40}
                                block
                                text=" Remote Preview"
                                icon={<FaLock />}
                                onClick={() => { }}
                                disabled
                                customTheme={themeConfig.primaryOutlined}
                            />
                        </div>
                    </Tooltip>

                </Col> */}

            </Row>
            )}
            <Row justify="center" align="middle" gutter={[6, 6]} style={{ padding:10, width: showPreview ? '80%' : '' }}>
                {/* <Col span={8}>
                    <Tooltip title="This feature is only available for organization accounts" placement="bottom">
                        <div style={{ width: '100%' }}>
                            <Button
                                minHeight={40}
                                block
                                text="Assign To Class"
                                icon={<FaLock />}
                                onClick={() => { }}
                                disabled
                                customTheme={themeConfig.primaryOutlined}
                            />
                        </div>
                    </Tooltip>

                </Col> */}
                {cloneText && (
                    <Col xxl={12} xl={12} sm={24} xs={24}>
                        <S.ActionButtonContainer>
                        <Button
                            minHeight={50}
                            block
                            text={cloneText}
                            onClick={cloneAction}
                            loading={loading}
                        // customTheme={themeConfig.primaryOutlined}
                        />
                        </S.ActionButtonContainer>
                    </Col>
                )}
                {actionText !== 'View Investigations' && (
                    showPreview && !isTeacher ? (
                        <>
                            <Col xxl={12} xl={12} sm={24} xs={24}>
                            <S.ActionButtonContainer>
                                <Button
                                    minHeight={50}
                                    block
                                    text="Preview"
                                    data-cy="components-investigation-card-preview-button"
                                    onClick={() => setPreviewOpen(true)}
                                    loading={loading}
                                    customTheme={themeConfig.primaryOutlined}
                                />
                                </S.ActionButtonContainer>
                            </Col>
                            <Col xxl={12} xl={12} sm={24} xs={24}>
                                <Popover
                                    placement="top"
                                    trigger={actionDisabled ? 'hover' : ''}
                                    content={
                                        <>
                                            <p style={{ marginBottom: 10 }}>
                                                {`Upgrade your subscription to enable this ${isAssessment ? 'assessment' : 'investigation'
                                                    }`}
                                            </p>
                                            <Button
                                                text="Upgrade"
                                                onClick={subscriptionPage}
                                                customTheme={themeConfig.secondaryColor}
                                                block
                                            />
                                        </>
                                    }
                                >
                                    <div style={{ width: '100%' }}>
                                        <S.ActionButtonContainer>
                                        <Button
                                            minHeight={50}
                                            block
                                            text={actionText}
                                            data-cy="components-investigation-card-action-button"
                                            onClick={action}
                                            disabled={actionDisabled}
                                            loading={loading}
                                            icon={actionDisabled ? <FiLock /> : <></>}
                                            customTheme={themeConfig.primaryColor}
                                        />
                                        </S.ActionButtonContainer>
                                    </div>
                                </Popover>
                            </Col>
                        </>
                    ) : (
                        !isTeacher && (
                            <Col xxl={12} xl={12} sm={24} xs={24}>
                                <Popover
                                    placement="top"
                                    trigger={actionDisabled ? 'hover' : ''}
                                    content={
                                        <>
                                            <p style={{ marginBottom: 10 }}>
                                                {`Upgrade your subscription to enable this ${isAssessment ? 'assessment' : 'investigation'
                                                    }`}
                                            </p>
                                            <Button
                                                text="Upgrade"
                                                onClick={subscriptionPage}
                                                customTheme={themeConfig.secondaryColor}
                                                block
                                            />
                                        </>
                                    }
                                >
                                    <div style={{ width: '100%' }}>
                                        <S.ActionButtonContainer>
                                        <Button
                                            minHeight={50}
                                            block
                                            text={actionText}
                                            data-cy="components-investigation-card-action-button"
                                            onClick={action}
                                            disabled={actionDisabled}
                                            loading={loading}
                                            icon={actionDisabled ? <FiLock /> : <></>}
                                            customTheme={themeConfig.primaryColor}
                                        />
                                        </S.ActionButtonContainer>
                                    </div>
                                </Popover>
                            </Col>
                        )
                    )
                )}

                {/* {!loadingIsTemplate && !isTeacher && actionText === "Edit Investigation" &&
                    <Col xxl={12} xl={12} sm={24} xs={24}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', paddingLeft: 10 }}>
                            <Popconfirm
                                placement="topLeft"
                                title={`Are you sure you want to ${isTemplate ? 'disable' : 'enable'} template?`}
                                onConfirm={handleToggleTemplate}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Switch
                                    type="text"
                                    title={<S.TitleInput>Enable as Template</S.TitleInput>}
                                    checked={isTemplate}
                                    value={isTemplate}
                                    titleAlign="left"
                                />
                            </Popconfirm>
                        </div>
                    </Col>} */}
            </Row>
            <NewInvestigationModal
                open={modalOpen}
                title={isAssessment ? 'Assessment' : 'Investigation'}
                onConfirm={handleUseTemplate}
                onClose={closeModal}
                loading={loadingCreation}
                subject={data.discipline?.subject}
                isCloning={isCloning}
                isAssessment={isAssessment}
            />
            <Modal visible={openMaterials} onCancel={() => setOpenMaterials(false)} footer={null} width={700}>
                <S.MaterialsModalContainer>
                    <h1>Investigation Downloads</h1>
                    <Spin spinning={downloading} size="large">
                        <Row>
                            <Col span={12} offset={6}>
                                Here you can find the downloadable materials for each stage
                            </Col>
                            <Col span={6}>
                                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '4rem' }}>
                                    <Button
                                        style={{ marginLeft: '-30.5rem' }}
                                        text="Download Material"
                                        customTheme={themeConfig.primaryColor}
                                        icon={<FiDownload />}
                                        onClick={downloadAllMaterials}
                                        disabled={downloading}
                                        loading={downloading}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Spin>
                </S.MaterialsModalContainer>
            </Modal>
            {/* <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', }}>
                <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px' }}>
                    <input
                        type="checkbox"
                        checked={toggleState}
                        onChange={() => setToggleState(!toggleState)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span
                        style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: toggleState ? '#1890ff' : '#ccc',
                            transition: '.4s',
                            borderRadius: '34px',
                        }}
                    />
                    <span
                        style={{
                            position: 'absolute',
                            content: '""',
                            height: '20px',
                            width: '20px',
                            left: toggleState ? '26px' : '4px',
                            bottom: '3px',
                            backgroundColor: 'white',
                            transition: '.4s',
                            borderRadius: '50%',
                            boxShadow: '0 0 2px rgba(0,0,0,0.2)',
                        }}
                    />
                </label>
                <span style={{ userSelect: 'none', fontWeight: '500' }}>
                    {toggleState ? 'Enable as template' : 'Disable as template'}
                </span>
            </div> */}
        </>
    );
};

export default InvestigationTab;